import { Component, Input, OnInit, ViewChild, ViewChildren, Output, EventEmitter, AfterViewInit, OnDestroy, QueryList, ChangeDetectorRef } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import * as View from 'esri/views/View';
import {
  Activity,
  ActivityModel,
  DataEntityTableLayoutModel,
  EntityTableCelllayout,
  EntityTableRowLayout
} from 'src/app/models/activities';
import { DataEntity } from 'src/app/models/data-entities';
import { DataEntityInputListComponent } from '../../data-entity-input-list/data-entity-input-list.component';
import { DataEntityInputComponent } from '../../data-entity-input/data-entity-input.component';
import { DataEntityViewListComponent } from '../../data-entity-view-list/data-entity-view-list.component';
import { empty, Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { LayoutService } from 'src/app/services/layout.service';

@Component({
  selector: 'wm-data-entity-table-layout-input',
  templateUrl: './data-entity-table-layout-input.component.html',
  styleUrl: './data-entity-table-layout-input.component.css',
  standalone: false
})
export class DataEntityTableLayoutInputComponent implements OnInit, AfterViewInit, OnDestroy {

  templateForPermitForm: boolean = false;
  private layoutSubscription: Subscription;

  constructor(public layoutService: LayoutService, private _ref: ChangeDetectorRef) {
    this.layoutSubscription = this.layoutService.useNewLayout$.subscribe(useNewLayout => {
      this.templateForPermitForm = useNewLayout;
    });
  }

  @Input() layoutModel: DataEntityTableLayoutModel;
  @Input() form: UntypedFormGroup;
  @Input() isReadOnly: boolean;
  @Input() applicationId: string;
  @Input() activity: Activity<ActivityModel>;
  @Output() calculateActivity: EventEmitter<{
    templateCode: string;
    calculateFormula?: boolean;
    visReq?: boolean;
  }> = new EventEmitter();
  @ViewChildren('dataEntityInput') dataEntityInputCtls: QueryList<DataEntityInputComponent>;
  @ViewChildren(DataEntityTableLayoutInputComponent) nestedTableLayouts: QueryList<DataEntityTableLayoutInputComponent>;

  private calculateSubscriptions: Subscription[] = [];

  ngOnInit(): void {
    // Initialize validation state and deserialize values for entities with dynamic columns
    if (this.layoutModel && this.layoutModel.rows) {
      this.layoutModel.rows.forEach(row => {
        row.cells.forEach(cell => {
          if (this.shouldRenderAsDynamicColumns(cell) && cell.entity) {
            const entity = cell.entity as any;

            // Deserialize selectedValues from entity.value if it's a JSON string
            if (entity.value && typeof entity.value === 'string' && entity.value !== '[]') {
              try {
                const parsed = JSON.parse(entity.value);
                if (Array.isArray(parsed) && parsed.length > 0) {
                  // Match parsed values with available list items to ensure full structure (id, value, text)
                  const availableItems = entity.availableListItems || entity.listTextValues || [];
                  entity.selectedValues = parsed.map(selectedItem => {
                    // Try to find the matching item in availableItems
                    const match = availableItems.find((available: any) => {
                      // Match by id
                      if (selectedItem.id && available.id && selectedItem.id === available.id) {
                        return true;
                      }
                      // Match by value
                      if (selectedItem.value && available.value && selectedItem.value === available.value) {
                        return true;
                      }
                      // Match by text
                      if (selectedItem.text && available.text && selectedItem.text === available.text) {
                        return true;
                      }
                      return false;
                    });
                    // Return the match if found, otherwise return the selectedItem as-is
                    return match || selectedItem;
                  });
                  entity.selectedItems = entity.selectedValues;
                }
              } catch (e) {
                // If parsing fails, keep selectedValues as is
                console.warn('Failed to parse entity.value for', entity.templateCode, e);
              }
            }

            // Ensure selectedValues exists
            if (!entity.selectedValues) {
              entity.selectedValues = [];
            }

            // Initialize validation state for list data entity
            if (entity.isRequired) {
              entity.isValid = (entity.selectedValues && entity.selectedValues.length > 0);
            } else {
              entity.isValid = true;
            }
          }
        });
      });
    }

    // Subscribe to changes of entities that participate in table or row
    // visible-criteria so we can re-evaluate visibility as values change.
    //
    // We union the server-provided `sameActivityDataEntities` with a
    // client-side derivation walking every table + row visibleCriteria.
    // This is needed because some hosting activities (e.g. Consolidated
    // Inspection Activity) don't reliably run PrepareForScreen on their
    // inner step activities' layouts, leaving `sameActivityDataEntities`
    // empty or incomplete. Without this, value changes on a criteria
    // entity in the same activity never trigger row visibility
    // re-evaluation.
    if (this.form) {
      const templateCodes = new Set<string>(
        this.layoutModel?.sameActivityDataEntities ?? []
      );

      this.collectCriteriaTemplateCodes(templateCodes);

      templateCodes.forEach(templateCode => {
        const ctl = this.form.controls[templateCode];

        if (ctl) {
          const sub = ctl.valueChanges
            .pipe(distinctUntilChanged())
            .subscribe(() => {
              this.reevaluateVisibility();
            });

          this.calculateSubscriptions.push(sub);
        }
      });
    }

    // Initial evaluation of table + row visibility.
    this.reevaluateVisibility();
  }

  /**
   * Walks table-level and row-level visibleCriteria on this layout model
   * and adds every referenced entity `field` (templateCode) to the
   * provided set. Recurses into nested condition groups.
   */
  private collectCriteriaTemplateCodes(target: Set<string>): void {
    if (!this.layoutModel) {
      return;
    }

    const visitGroup = (group: any) => {
      if (!group || !Array.isArray(group.conditions)) {
        return;
      }
      group.conditions.forEach((c: any) => {
        if (c?.isGroup === true) {
          visitGroup(c.group);
        } else if (c?.field) {
          target.add(c.field);
        }
      });
    };

    if (this.layoutModel.visibleCriteria?.group) {
      visitGroup(this.layoutModel.visibleCriteria.group);
    }

    if (this.layoutModel.rows) {
      this.layoutModel.rows.forEach(row => {
        if (row?.visibleCriteria?.group) {
          visitGroup(row.visibleCriteria.group);
        }
      });
    }
  }

  private reevaluateVisibility(): void {
    if (!this.layoutModel || !this.activity?.model) {
      return;
    }

    // Defer to a microtask so that other subscribers on the same
    // FormControl.valueChanges observable (notably wm-common-dropdown's
    // re-emitter that eventually invokes list-data-entity-input.handleDropdownChange)
    // get a chance to mutate the underlying entity model before we snapshot
    // entity values for criteria evaluation. Without this, an ordering race
    // causes the table to read stale entity.value and hide rows that should
    // become visible.
    Promise.resolve().then(() => {
      if (!this.layoutModel || !this.activity?.model) {
        return;
      }

      const entities = this.activity.model.getEntities();

      this.layoutModel.evaluateIsVisible(entities, this.activity).subscribe(() => {
        this._ref.detectChanges();
      });

      if (this.layoutModel.rows) {
        this.layoutModel.rows.forEach((row: EntityTableRowLayout) => {
          this.layoutModel.evaluateRowIsVisible(row, entities, this.activity).subscribe(() => {
            this._ref.detectChanges();
          });
        });
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.dataEntityInputCtls) {
      this.dataEntityInputCtls.forEach(entityCtl => {
        const subscription = entityCtl.calculateActivity.subscribe(e => {
          this.calculateActivity.emit(e);
        });
        this.calculateSubscriptions.push(subscription);
      });
    }

    // Subscribe to nested table layout events
    if (this.nestedTableLayouts) {
      this.nestedTableLayouts.forEach(nestedLayout => {
        const subscription = nestedLayout.calculateActivity.subscribe(e => {
          this.calculateActivity.emit(e);
        });
        this.calculateSubscriptions.push(subscription);
      });
    }
  }

  ngOnDestroy(): void {
    this.calculateSubscriptions.forEach(subscription => {
      if (subscription) {
        subscription.unsubscribe();
      }
    });

    // Clean up the layout subscription added for templateForPermitForm switching.
    if (this.layoutSubscription) {
      this.layoutSubscription.unsubscribe();
    }
  }

  cancelCalculation(templateCode: string) {
    if (this.dataEntityInputCtls) {
      const entityCtl = this.dataEntityInputCtls.find(
        ctl => ctl.entity?.templateCode === templateCode
      );

      if (entityCtl) {
        entityCtl.cancelCalculation();
      }
    }
  }

  async persistChildComponent?() {
    const promises: Promise<any>[] = [];

    if (this.dataEntityInputCtls) {
      this.dataEntityInputCtls.forEach(entityCtl => {
        if (entityCtl.persistChildComponent) {
          promises.push(entityCtl.persistChildComponent());
        }
      });
    }

    // Persist nested table layouts
    if (this.nestedTableLayouts) {
      this.nestedTableLayouts.forEach(nestedLayout => {
        if (nestedLayout.persistChildComponent) {
          promises.push(nestedLayout.persistChildComponent());
        }
      });
    }

    if (promises.length > 0) {
      return Promise.all(promises);
    }
    else {
      return Promise.resolve();
    }
  }

  // Dynamic column support for List Data Entities
  shouldRenderAsDynamicColumns(cell: EntityTableCelllayout): boolean {
    return !!(cell.renderListItemsAsDynamicColumns &&
              cell.entity &&
              cell.entity.dataEntityTypeCode === 'list-data');
  }

  getListItems(entity: any): any[] {
    return entity.availableListItems || entity.listTextValues || [];
  }

  getListControlType(entity: any): 'radio' | 'checkbox' {
    // controlType can be 'Radio' or 'radio' (enum) or 'Check'/'checkbox'/'MultiSelect'
    const controlType = entity.controlType;
    if (!controlType) return 'checkbox';

    // Handle both enum values and legacy string values
    const type = controlType.toString().toLowerCase();
    return type === 'radio' ? 'radio' : 'checkbox';
  }

  getListInputName(entity: any): string {
    // Generate unique name for radio button grouping
    return `list_${entity.templateCode}_${entity.id || 'new'}`;
  }

  getListItemId(entity: any, listItem: any): string {
    // Generate unique ID for each control
    const itemId = listItem.id || listItem.value || listItem;
    return `list_${entity.templateCode}_${entity.id || 'new'}_${itemId}`;
  }

  isListItemSelected(entity: any, listItem: any): boolean {
    // Check if this list item is currently selected
    const itemId = listItem.id || listItem.value || listItem;
    const itemValue = listItem.value;
    const itemText = listItem.text;

    // Check selectedValues array
    if (entity.selectedValues && Array.isArray(entity.selectedValues)) {
      return entity.selectedValues.some((val: any) => {
        // Compare by id first (most reliable)
        if (val.id && itemId && val.id === itemId) {
          return true;
        }
        // Compare by value
        if (val.value && itemValue && val.value === itemValue) {
          return true;
        }
        // Compare by text as fallback
        if (val.text && itemText && val.text === itemText) {
          return true;
        }
        // Direct comparison
        const selectedValue = val.id || val.value || val;
        return selectedValue === itemId;
      });
    }

    // Check selectedItems array (legacy support)
    if (entity.selectedItems && Array.isArray(entity.selectedItems)) {
      return entity.selectedItems.some((item: any) => {
        // Compare by id first (most reliable)
        if (item.id && itemId && item.id === itemId) {
          return true;
        }
        // Compare by value
        if (item.value && itemValue && item.value === itemValue) {
          return true;
        }
        // Compare by text as fallback
        if (item.text && itemText && item.text === itemText) {
          return true;
        }
        // Direct comparison
        const selectedValue = item.id || item.value || item;
        return selectedValue === itemId;
      });
    }

    return false;
  }

  onListItemChange(entity: any, listItem: any, event: any): void {
    const isChecked = event.target?.checked;
    const itemValue = listItem.id || listItem.value || listItem;

    // Initialize selectedValues if needed
    if (!entity.selectedValues) {
      entity.selectedValues = [];
    }

    // Check if this is a radio button (handle both legacy 'Radio' and enum 'radio')
    const controlType = entity.controlType?.toString().toLowerCase();
    const isRadio = controlType === 'radio';

    if (isRadio) {
      // Radio: single selection, replace entire array
      if (isChecked) {
        entity.selectedValues = [listItem];
      }
    } else {
      // Checkbox: multi-selection, add/remove from array
      const existingIndex = entity.selectedValues.findIndex((val: any) => {
        const selectedValue = val.id || val.value || val;
        return selectedValue === itemValue;
      });

      if (isChecked && existingIndex === -1) {
        // Add to selection
        entity.selectedValues.push(listItem);
      } else if (!isChecked && existingIndex !== -1) {
        // Remove from selection
        entity.selectedValues.splice(existingIndex, 1);
      }
    }

    // Sync selectedItems for legacy compatibility
    entity.selectedItems = [...entity.selectedValues];

    // Remove $id attributes before serializing
    entity.selectedValues.forEach((item: any) => delete item['$id']);

    // Update entity value (serialized JSON)
    entity.value = JSON.stringify(entity.selectedValues);

    // Update form control if it exists
    if (this.form && this.form.controls[entity.templateCode]) {
      if (entity.value === '[]' || entity.selectedValues.length === 0) {
        this.form.controls[entity.templateCode].setValue('');
      } else {
        this.form.controls[entity.templateCode].setValue(entity.value);
      }
      this.form.controls[entity.templateCode].markAsDirty();
      this.form.controls[entity.templateCode].updateValueAndValidity();
    }

    // Update entity validation state
    if (entity.isRequired) {
      // Mark as valid if there's at least one selection, invalid if empty
      entity.isValid = entity.selectedValues.length > 0;
    } else {
      // Not required, always valid
      entity.isValid = true;
    }

    // Emit calculate event to trigger parent form validation updates
    this.calculateActivity.emit({
      templateCode: entity.templateCode,
      calculateFormula: false,
      visReq: true
    });
  }


  onListItemCommonCheckboxChange(entity: any, listItem: any, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    const normalizedEvent = { target: { checked } };
    this.onListItemChange(entity, listItem, normalizedEvent);
  }


  onListItemCommonRadioChange(entity: any, listItem: any, value: any): void {
    if (value === null || value === undefined) return;
    const normalizedEvent = { target: { checked: true } };
    this.onListItemChange(entity, listItem, normalizedEvent);
  }

  //added by furkan for required fields in table layout
  isDynamicListRequiredAndEmpty(entity: any): boolean {
    if (!entity || !entity.isRequired) return false;
    if (entity.selectedValues && Array.isArray(entity.selectedValues) && entity.selectedValues.length > 0) {
      return false;
    }
    return !entity.value || entity.value === '[]' || entity.value === '';
  }

  private isEntityRequiredAndEmpty(entity: any): boolean {
    if (!entity || !entity.isRequired) return false;

    if (this.form && this.form.controls[entity.templateCode]) {
      const val = this.form.controls[entity.templateCode].value;
      if (val !== null && val !== undefined && String(val).trim() !== '' && val !== '[]') {
        return false;
      }
      return true;
    }

    const entityVal = entity.value ?? entity.calculatedValue;
    if (entityVal !== null && entityVal !== undefined && String(entityVal).trim() !== '' && entityVal !== '[]') {
      return false;
    }
    return true;
  }

  isFieldRequiredAndEmpty(cell: EntityTableCelllayout): boolean {
    if (this.isReadOnly) return false;

    if (cell.entity && this.isEntityRequiredAndEmpty(cell.entity)) {
      return true;
    }

    if (cell.layoutModel?.entity && this.isEntityRequiredAndEmpty(cell.layoutModel.entity)) {
      return true;
    }

    return false;
  }
  //ended by furkan

  // Get cells that should be rendered for a specific row, accounting for rowspan
  getCellsForRow(rowIndex: number): any[] {
    const row = this.layoutModel.rows[rowIndex];
    if (!row) return [];

    // The data model should already have the correct cells for each row
    // The rowspan attribute in the HTML will handle the spanning
    return row.cells;
  }

  // Calculate the actual HTML rowspan accounting for validation rows
  getAdjustedRowSpan(rowIndex: number, cell: any): number {
    const baseRowSpan = cell.rowSpan || 1;

    // Count how many validation rows exist between this row and the last row spanned
    let validationRowCount = 0;
    for (let i = rowIndex; i < rowIndex + baseRowSpan && i < this.layoutModel.rows.length; i++) {
      if (!this.isReadOnly && this.hasInvalidDynamicColumns(this.layoutModel.rows[i])) {
        validationRowCount++;
      }
    }

    return baseRowSpan + validationRowCount;
  }

  // Check if a row has any invalid dynamic columns
  hasInvalidDynamicColumns(row: any): boolean {
    if (!row || !row.cells) return false;

    return row.cells.some((cell: any) => {
      return this.shouldRenderAsDynamicColumns(cell) &&
             cell.entity &&
             cell.entity.isRequired &&
             !cell.entity.isValid;
    });
  }

  // Calculate total column count for a row (for colspan)
  getTotalColumnCount(row: any): number {
    if (!row || !row.cells) return 1;

    let total = 0;
    row.cells.forEach((cell: any) => {
      if (this.shouldRenderAsDynamicColumns(cell)) {
        // Dynamic columns - count list items
        total += this.getListItems(cell.entity).length;
      } else {
        // Normal cell - use colspan
        total += (cell.colspan || 1);
      }
    });

    return total || 1;
  }

  shouldShowRequiredMarkerForStaticCell(rowIndex: number, cellIndex: number): boolean {
    if (this.isReadOnly) {
      return false;
    }

    const row = this.layoutModel?.rows?.[rowIndex];
    const cell = row?.cells?.[cellIndex];

    if (!cell?.staticText || cell.entity || cell.layoutModel) {
      return false;
    }

    return row.cells
      .slice(cellIndex + 1)
      .some(nextCell => this.cellContainsRequiredEntity(nextCell));
  }

  // Added by Amisha on 03-07-2026.
  // Reason: Janus table-layout labels are rendered as static-text cells, so
  // this helper detects whether the corresponding row contains a required entity.
  private cellContainsRequiredEntity(cell: EntityTableCelllayout): boolean {
    if (cell?.entity?.isRequired && cell.entity.isVisible !== false) {
      return true;
    }

    return this.layoutModelContainsRequiredEntity(cell?.layoutModel);
  }

  private layoutModelContainsRequiredEntity(layoutModel?: any): boolean {
    if (!layoutModel) {
      return false;
    }

    if (layoutModel.entity?.isRequired && layoutModel.entity.isVisible !== false) {
      return true;
    }

    if (Array.isArray(layoutModel.rows)) {
      return layoutModel.rows.some((row: any) =>
        (row?.cells || []).some((nestedCell: EntityTableCelllayout) =>
          this.cellContainsRequiredEntity(nestedCell)
        )
      );
    }

    if (Array.isArray(layoutModel.groupEntities)) {
      return layoutModel.groupEntities.some((groupEntity: any) =>
        this.layoutModelContainsRequiredEntity(groupEntity)
      );
    }

    if (Array.isArray(layoutModel.rowEntities)) {
      return layoutModel.rowEntities.some((rowEntity: any) =>
        this.layoutModelContainsRequiredEntity(rowEntity)
      );
    }

    return false;
  }
}
